import React, { useState, useRef, useEffect } from 'react';
import { 
  Button, 
  Box, 
  Typography, 
  CircularProgress, 
  Dialog, 
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Chip,
  Paper,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Image as ImageIcon, Close as CloseIcon, NavigateBefore as NavigateBeforeIcon, NavigateNext as NavigateNextIcon, Save as SaveIcon, Delete as DeleteIcon, Warning as WarningIcon, CheckCircle, RadioButtonUnchecked, SmartToy as SmartToyIcon, Settings as SettingsIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, MoreVert as MoreVertIcon, Cancel as CancelIcon, Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material';
import api from '../../../services/api';
import ImageGrid from '../shared/ImageGrid';
import AIChat, { DRAWER_WIDTH } from '../../chat/AIChat';

export default function BlocImage({ block, sessionId, sequenceIndex, sequence, onRefresh, onSave, openRegenerateDialogForBlock = null, onRegenerateDialogOpened = null }) {
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Image regeneration modal state
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [regeneratePrompt, setRegeneratePrompt] = useState('');
  const [regenerateModel, setRegenerateModel] = useState('google/gemini-2.5-flash-image');
  const [regenerateStyle, setRegenerateStyle] = useState('cartoon');
  const [regenerateAspectRatio, setRegenerateAspectRatio] = useState('16:9');
  const [regenerateNbVariations, setRegenerateNbVariations] = useState(2);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [imageEditingModels, setImageEditingModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  // Image titles state - map of image index to title
  const [imageTitles, setImageTitles] = useState(new Map());
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [editingTitleIndex, setEditingTitleIndex] = useState(null);
  
  // AI Chat state
  const [aiChatOpen, setAiChatOpen] = useState(true);
  
  // Advanced parameters state
  const [showAdvancedParams, setShowAdvancedParams] = useState(false);
  
  // Image validation state
  const [imageFilter, setImageFilter] = useState('validated'); // 'all', 'validated', 'not-validated'
  const [selectedImageIndices, setSelectedImageIndices] = useState(new Set());
  const [isSavingValidation, setIsSavingValidation] = useState(false);
  const [pendingImageValidations, setPendingImageValidations] = useState(new Map()); // Map<index, status>
  
  // Image deletion state
  const [deleteImagesDialogOpen, setDeleteImagesDialogOpen] = useState(false);
  const [isDeletingImages, setIsDeletingImages] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  
  // Menu state
  const [imageMenuAnchor, setImageMenuAnchor] = useState(null);
  
  // Image reordering state (drag and drop)
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [reorderedImages, setReorderedImages] = useState(null); // null means no reordering, array means pending reorder
  const isProcessingDropRef = useRef(false); // Use ref instead of state for synchronous checking
  const lastDropTimestampRef = useRef(0); // Track last drop time to prevent rapid successive drops
  const draggedIndexRef = useRef(null); // Track dragged index in ref to avoid stale closures
  
  // Get images from bloc_data
  const originalImages = block.bloc_data?.images || block.content?.images || [];
  // Use reordered images if available, otherwise use original
  const images = reorderedImages || originalImages;
  // Get bloc_index from block
  const blocIndex = block.bloc_index;
  
  // Get effective validation status (considering pending changes)
  const getEffectiveValidationStatus = (index) => {
    const pendingStatus = pendingImageValidations.get(index);
    if (pendingStatus !== undefined) {
      return pendingStatus === 'validated';
    }
    return images[index]?.status_seance === 'validated';
  };
  
  // Get original indices of filtered images
  const getOriginalIndex = (filteredIndex) => {
    // If showing all images, return the index directly
    if (imageFilter === 'all') {
      return filteredIndex;
    }
    
    let count = 0;
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      const isValidated = image.status_seance === 'validated';
      let shouldInclude = true;
      if (imageFilter === 'validated') shouldInclude = isValidated;
      if (imageFilter === 'not-validated') shouldInclude = !isValidated;
      
      if (shouldInclude) {
        if (count === filteredIndex) return i;
        count++;
      }
    }
    return filteredIndex;
  };

  // Handle carousel navigation
  const handleOpenCarousel = (imageIndex) => {
    setCurrentImageIndex(imageIndex);
    setCarouselOpen(true);
  };

  const handleCloseCarousel = () => {
    setCarouselOpen(false);
    setCurrentImageIndex(0);
  };

  // Handle image regeneration modal
  const handleOpenRegenerateDialog = (imageIndex) => {
    setSelectedImageIndex(imageIndex);
    const images = block.bloc_data?.images || block.content?.images || [];
    const selectedImage = images[imageIndex];
    // Try to get the original prompt from the image metadata or block data
    setRegeneratePrompt(block.bloc_data?.prompt || block.content?.prompt || '');
    
    // Initialize image titles from existing images (only use title, not description)
    const titlesMap = new Map();
    images.forEach((image, idx) => {
      titlesMap.set(idx, image.title || '');
    });
    setImageTitles(titlesMap);
    
    setRegenerateDialogOpen(true);
  };

  // Effect to open regenerate dialog when triggered from parent
  useEffect(() => {
    if (openRegenerateDialogForBlock && openRegenerateDialogForBlock.blockId === block.id) {
      const images = block.bloc_data?.images || block.content?.images || [];
      // Wait for images to be available (they might be loading after generation)
      if (images.length > 0) {
        // Use the specified image index, or default to the last image
        const imageIndex = openRegenerateDialogForBlock.imageIndex !== undefined 
          ? openRegenerateDialogForBlock.imageIndex 
          : images.length - 1;
        // Ensure index is within bounds
        const safeIndex = Math.min(Math.max(0, imageIndex), images.length - 1);
        // Small delay to ensure the block is fully rendered
        setTimeout(() => {
          handleOpenRegenerateDialog(safeIndex);
          // Notify parent that dialog has been opened
          if (onRegenerateDialogOpened) {
            onRegenerateDialogOpened();
          }
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openRegenerateDialogForBlock, 
    block.id, 
    block.bloc_data?.images?.length, // Use length instead of array reference
    block.content?.images?.length
  ]);

  // Fetch image editing models on component mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setModelsLoading(true);
        const models = await api.aiModels.getAIModels('image_editing_models');
        const modelsArray = Array.isArray(models) ? models : [];
        setImageEditingModels(modelsArray);
        if (modelsArray.length > 0) {
          setRegenerateModel(modelsArray[0].id);
        }
      } catch (err) {
        console.error('Error fetching image editing models:', err);
        setImageEditingModels([]);
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  }, []);

  // Effect to update image titles when images change (e.g., after refresh)
  useEffect(() => {
    if (regenerateDialogOpen && images.length > 0) {
      const titlesMap = new Map(imageTitles);
      images.forEach((image, idx) => {
        // Only update if we don't already have a title for this index (to preserve user edits)
        // Only use image.title, not description
        if (!titlesMap.has(idx) && image.title) {
          titlesMap.set(idx, image.title || '');
        }
      });
      // Only update if there are new titles to add
      if (titlesMap.size > imageTitles.size) {
        setImageTitles(titlesMap);
      }
    }
  }, [images, regenerateDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset editing state when selected image changes
  useEffect(() => {
    if (editingTitleIndex !== null && editingTitleIndex !== selectedImageIndex) {
      setEditingTitleIndex(null);
    }
  }, [selectedImageIndex, editingTitleIndex]);

  const handleCloseRegenerateDialog = () => {
    setRegenerateDialogOpen(false);
    setRegeneratePrompt('');
    setRegenerateModel('google/gemini-2.5-flash-image');
    setRegenerateStyle('cartoon');
    setRegenerateAspectRatio('16:9');
    setRegenerateNbVariations(2);
    setShowAdvancedParams(false);
  };

  // Validate/unvalidate the current image directly
  const handleValidateImage = async () => {
    if (!onSave) return;
    
    const images = block.bloc_data?.images || block.content?.images || [];
    if (selectedImageIndex >= images.length) return;
    
    try {
      const currentImage = images[selectedImageIndex];
      const isCurrentlyValidated = currentImage?.status_seance === 'validated';
      
      const updatedImages = images.map((image, index) => {
        if (index === selectedImageIndex) {
          // Toggle validation status
          return { ...image, status_seance: isCurrentlyValidated ? null : 'validated' };
        }
        return image;
      });
      
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          images: updatedImages
        }
      };
      
      await onSave(updatedBlock);
      
      if (onRefresh) {
        await onRefresh();
      }
      
      // Keep the dialog open - don't close it
    } catch (error) {
      console.error('Error validating image:', error);
      alert('Erreur lors de la validation de l\'image. Veuillez réessayer.');
    }
  };

  // Save image title handler
  const handleSaveImageTitle = async (imageIndex) => {
    if (!sessionId || blocIndex === null || blocIndex === undefined || !onSave) return;
    
    const title = imageTitles.get(imageIndex) || '';
    setIsSavingTitle(true);
    
    try {
      const images = block.bloc_data?.images || block.content?.images || [];
      if (imageIndex >= images.length) return;
      
      const updatedImages = images.map((image, idx) => {
        if (idx === imageIndex) {
          return { ...image, title: title.trim() || null };
        }
        return image;
      });
      
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          images: updatedImages
        }
      };
      
      await onSave(updatedBlock);
      
      // Refresh the seance data
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error saving image title:', error);
      alert('Erreur lors de la sauvegarde du titre. Veuillez réessayer.');
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Toggle favorite handler
  const handleToggleFavorite = async (imageIndex = null) => {
    if (!onSave || !sessionId || blocIndex === null || blocIndex === undefined) return;
    
    const images = block.bloc_data?.images || block.content?.images || [];
    const targetIndex = imageIndex !== null ? imageIndex : selectedImageIndex;
    if (targetIndex >= images.length) return;
    
    const currentImage = images[targetIndex];
    const isFavorite = currentImage?.favourite === true;
    const newFavoriteStatus = !isFavorite;
    
    try {
      // Update image favorite status
      const updatedImages = images.map((image, idx) => {
        if (idx === targetIndex) {
          return { ...image, favourite: newFavoriteStatus };
        }
        return image;
      });
      
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          images: updatedImages
        }
      };
      
      await onSave(updatedBlock);
      
      // If setting to favorite (true), also add to gallery
      if (newFavoriteStatus) {
        const imageUrl = currentImage?.img_url || currentImage?.compressed_url || currentImage?.url || '';
        const imageTitle = imageTitles.get(targetIndex) || currentImage?.title || '';
        const nameToSend = (imageTitle && imageTitle.trim()) || null;
        
        if (imageUrl) {
          try {
            await api.galleryImage.addImageToGallery(imageUrl, nameToSend);
          } catch (error) {
            console.error('Error adding image to gallery:', error);
            // Don't show error to user, just log it
          }
        }
      }
      
      // Refresh the seance data
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Erreur lors de la mise à jour du favori. Veuillez réessayer.');
    }
  };

  const handleRegenerateImage = async () => {
    if (!regeneratePrompt.trim() || !sessionId || blocIndex === null || blocIndex === undefined) return;
    
    setIsRegenerating(true);
    try {
      // Get the actual image index from the selected image
      const selectedImage = images[selectedImageIndex];
      const imageIndex = selectedImage?.image_index ?? selectedImageIndex;
      
      // Call API to regenerate the specific image
      await api.seance.updateImageBlock(
        sessionId,
        blocIndex,
        sequenceIndex,
        imageIndex,
        regeneratePrompt,
        regenerateAspectRatio,
        regenerateModel
      );
      
      // Refresh the block data
      if (onRefresh) {
        await onRefresh();
      }
      
      handleCloseRegenerateDialog();
    } catch (error) {
      console.error('Error regenerating image:', error);
      alert('Erreur lors de la régénération de l\'image. Veuillez réessayer.');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Image validation handlers
  const handleToggleImageSelection = (originalIndex) => {
    // Don't allow selection of validated images
    const image = images[originalIndex];
    if (image && image.status_seance === 'validated') {
      return;
    }
    
    setSelectedImageIndices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(originalIndex)) {
        newSet.delete(originalIndex);
      } else {
        newSet.add(originalIndex);
      }
      return newSet;
    });
  };

  // Toggle validation for a single image
  const handleToggleImageValidation = (originalIndex) => {
    setPendingImageValidations(prev => {
      const newMap = new Map(prev);
      const currentImage = images[originalIndex];
      const currentStatus = currentImage.status_seance;
      const pendingStatus = newMap.get(originalIndex);
      
      // If there's a pending change, remove it (revert)
      if (pendingStatus !== undefined) {
        newMap.delete(originalIndex);
      } else {
        // Toggle validation status
        const newStatus = currentStatus === 'validated' ? null : 'validated';
        newMap.set(originalIndex, newStatus);
      }
      return newMap;
    });
  };

  // Save all pending validations and reordering to backend
  const handleSaveValidations = async () => {
    if ((pendingImageValidations.size === 0 && !reorderedImages) || !onSave) return;
    
    setIsSavingValidation(true);
    try {
      let updatedImages = images.map((image, index) => {
        const pendingStatus = pendingImageValidations.get(index);
        if (pendingStatus !== undefined) {
          return { ...image, status_seance: pendingStatus };
        }
        return image;
      });
      
      // Update image_index for reordered images
      if (reorderedImages) {
        updatedImages = updatedImages.map((image, index) => ({
          ...image,
          image_index: index
        }));
      }
      
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          images: updatedImages
        }
      };
      
      await onSave(updatedBlock);
      setPendingImageValidations(new Map());
      setReorderedImages(null); // Clear reordering after save
      
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error saving validations:', error);
      alert('Erreur lors de la sauvegarde des validations. Veuillez réessayer.');
    } finally {
      setIsSavingValidation(false);
    }
  };
  
  // Drag and drop handlers for image reordering
  const handleDragStart = (e, imageIndex) => {
    if (isDeleteMode || isProcessingDropRef.current) {
      e.preventDefault();
      return;
    }
    setDraggedImageIndex(imageIndex);
    draggedIndexRef.current = imageIndex; // Track in ref too
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, imageIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (draggedImageIndex !== null && draggedImageIndex !== imageIndex && !isProcessingDropRef.current) {
      setDragOverIndex(imageIndex);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    const timeSinceLastDrop = now - lastDropTimestampRef.current;
    
    // Prevent duplicate drops using multiple checks:
    // 1. Check if already processing (ref for immediate access)
    // 2. Check if drop happened too soon after last drop (< 300ms)
    // 3. Check if draggedImageIndex is valid
    // 4. Check if dropping on itself
    if (
      isProcessingDropRef.current || 
      timeSinceLastDrop < 300 ||
      draggedIndexRef.current === null || 
      draggedImageIndex === null || 
      draggedImageIndex === dropIndex
    ) {
      setDragOverIndex(null);
      setDraggedImageIndex(null);
      draggedIndexRef.current = null;
      return;
    }
    
    // Set flags immediately (synchronously)
    isProcessingDropRef.current = true;
    lastDropTimestampRef.current = now;
    
    // Capture the dragged index from ref (to avoid closure issues)
    const sourceIndex = draggedIndexRef.current;
    
    // Clear drag state immediately to prevent any re-triggers
    setDraggedImageIndex(null);
    setDragOverIndex(null);
    draggedIndexRef.current = null;
    
    try {
      // Create new array with reordered images
      const newImages = [...images];
      const draggedImage = newImages[sourceIndex];
      
      // Validate that we have a valid image to move
      if (!draggedImage) {
        console.error('Invalid dragged image at index:', sourceIndex);
        return;
      }
      
      // Remove dragged image from its position first
      newImages.splice(sourceIndex, 1);
      
      // Calculate the correct insertion index
      // After removing the dragged item, indices shift
      let insertIndex = dropIndex;
      if (sourceIndex < dropIndex) {
        // If moving forward, the drop index shifts left by 1 after removal
        insertIndex = dropIndex - 1;
      }
      // If moving backward, the drop index stays the same
      
      // Insert at the target position (before the item at insertIndex)
      newImages.splice(insertIndex, 0, draggedImage);
      
      // Update reordered images
      setReorderedImages(newImages);
    } catch (error) {
      console.error('Error during drag and drop:', error);
    } finally {
      // Reset processing flag after a delay to ensure all events are cleared
      setTimeout(() => {
        isProcessingDropRef.current = false;
      }, 300);
    }
  };

  const handleDragEnd = () => {
    // Only clear if not processing a drop (drop handler will clear it)
    if (!isProcessingDropRef.current) {
      setDraggedImageIndex(null);
      setDragOverIndex(null);
      draggedIndexRef.current = null;
    }
  };

  // Image deletion handlers
  const handleOpenDeleteImagesDialog = () => {
    if (selectedImageIndices.size === 0) return;
    setDeleteImagesDialogOpen(true);
  };

  const handleCloseDeleteImagesDialog = () => {
    setDeleteImagesDialogOpen(false);
  };

  const handleDeleteSelectedImages = async () => {
    if (selectedImageIndices.size === 0 || !onSave) return;
    
    setIsDeletingImages(true);
    try {
      // Filter out validated images from selection (shouldn't happen, but safety check)
      const validIndicesToDelete = Array.from(selectedImageIndices).filter(index => {
        const image = images[index];
        return image && image.status_seance !== 'validated';
      });
      
      if (validIndicesToDelete.length === 0) {
        alert('Aucune image non validée à supprimer. Les images validées ne peuvent pas être supprimées.');
        setIsDeletingImages(false);
        return;
      }
      
      // Convert to sorted array to delete from highest index to lowest (to avoid index shifting issues)
      const indicesToDelete = validIndicesToDelete.sort((a, b) => b - a);
      
      // Create new images array without the selected images
      let updatedImages = images.filter((image, index) => !validIndicesToDelete.includes(index));
      
      // Update image_index for each remaining image to be sequential (0, 1, 2, ...)
      updatedImages = updatedImages.map((image, index) => ({
        ...image,
        image_index: index
      }));
      
      // Update block with new images array
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          images: updatedImages
        }
      };
      
      // Save the updated block
      await onSave(updatedBlock);
      
      // Clear selection, exit delete mode, and close dialog
      setSelectedImageIndices(new Set());
      setIsDeleteMode(false);
      setDeleteImagesDialogOpen(false);
      
      // Clear any pending validations for deleted images
      setPendingImageValidations(prev => {
        const newMap = new Map(prev);
        indicesToDelete.forEach(index => {
          newMap.delete(index);
        });
        return newMap;
      });
      
      // Refresh the block data
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error deleting images:', error);
      alert('Erreur lors de la suppression des images. Veuillez réessayer.');
    } finally {
      setIsDeletingImages(false);
    }
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  return (
    <div style={{ padding: '16px' }}>
      {/* Images Gallery */}
      {images && images.length > 0 && (
        <Box sx={{ mt: 3 }}>

          {/* Delete mode buttons and validation action buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {isDeleteMode && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setIsDeleteMode(false);
                    setSelectedImageIndices(new Set());
                  }}
                  sx={{
                    borderColor: '#95A5A6',
                    color: '#7F8C8D',
                    borderRadius: '20px',
                    textTransform: 'none'
                  }}
                >
                  Annuler
                </Button>
                
                {selectedImageIndices.size > 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleOpenDeleteImagesDialog}
                    disabled={isDeletingImages}
                    sx={{
                      background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
                      borderRadius: '20px',
                      textTransform: 'none'
                    }}
                  >
                    Confirmer la suppression ({selectedImageIndices.size})
                  </Button>
                )}
              </>
            )}
            
            {/* Save button - Available when there are pending validations or reordering */}
            {(pendingImageValidations.size > 0 || reorderedImages) && (
              <Button
                variant="contained"
                size="small"
                startIcon={isSavingValidation ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSaveValidations}
                disabled={isSavingValidation}
                sx={{
                  background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                  borderRadius: '20px',
                  textTransform: 'none',
                  ml: 'auto'
                }}
              >
                {isSavingValidation ? 'Sauvegarde...' : `Sauvegarder (${pendingImageValidations.size + (reorderedImages ? 1 : 0)})`}
              </Button>
            )}
          </Box>

          <ImageGrid
            images={images}
            imageFilter={imageFilter}
            isDeleteMode={isDeleteMode}
            selectedImageIndices={selectedImageIndices}
            pendingImageValidations={pendingImageValidations}
            draggedImageIndex={draggedImageIndex}
            dragOverIndex={dragOverIndex}
            getEffectiveValidationStatus={getEffectiveValidationStatus}
            getOriginalIndex={getOriginalIndex}
            handleDragStart={handleDragStart}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handleDragEnd={handleDragEnd}
            handleToggleImageSelection={handleToggleImageSelection}
            handleToggleImageValidation={handleToggleImageValidation}
            handleOpenRegenerateDialog={handleOpenRegenerateDialog}
            imageUrlField="img_url"
            imageMenuAnchor={imageMenuAnchor}
            onMenuOpen={(e) => setImageMenuAnchor(e.currentTarget)}
            onMenuClose={() => setImageMenuAnchor(null)}
            onFilterChange={(filter) => setImageFilter(filter)}
            onDeleteModeToggle={() => {
              setIsDeleteMode(true);
              setSelectedImageIndices(new Set());
            }}
          />
        </Box>
      )}
      
      {(!images || images.length === 0) && (
          <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
            No images generated yet
          </Typography>
        )}


      {/* Image Carousel Modal */}
      <Dialog
        open={carouselOpen}
        onClose={handleCloseCarousel}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            borderRadius: '12px'
          }
        }}
      >
        <Box sx={{ position: 'relative', p: 2 }}>
          <IconButton
            onClick={handleCloseCarousel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 1,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
            {images.length > 0 && (
              <>
          <IconButton
            onClick={handlePreviousImage}
            sx={{
              position: 'absolute',
              left: 16,
              color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)'
                    },
                    zIndex: 1
            }}
          >
            <NavigateBeforeIcon />
          </IconButton>

                <Box sx={{ position: 'relative', maxWidth: '90%', maxHeight: '80vh' }}>
                  <Box
                    component="img"
                    src={images[currentImageIndex]?.img_url || images[currentImageIndex]?.original_url || images[currentImageIndex]?.url}
                    alt={images[currentImageIndex]?.description || `Image ${currentImageIndex + 1}`}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '80vh',
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x600?text=Image+Error';
                    }}
                  />
                  
                  {/* Validation button in carousel - top-right corner */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleImageValidation(currentImageIndex);
                    }}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      zIndex: 10,
                      bgcolor: 'white',
                      width: '32px',
                      height: '32px',
                      minWidth: '32px',
                      padding: 0,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      '&:hover': {
                        bgcolor: '#F5F5F5',
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    {getEffectiveValidationStatus(currentImageIndex) ? (
                      <CheckCircle sx={{ color: '#27AE60', fontSize: '32px' }} />
                    ) : (
                      <RadioButtonUnchecked sx={{ color: '#95A5A6', fontSize: '32px' }} />
                    )}
                  </IconButton>
                  
                  {images[currentImageIndex]?.description && (
                    <Paper
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        maxWidth: '100%'
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#2C3E50' }}>
                        {images[currentImageIndex].description}
                      </Typography>
                    </Paper>
                  )}
                </Box>

          <IconButton
            onClick={handleNextImage}
            sx={{
              position: 'absolute',
              right: 16,
              color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)'
                    },
                    zIndex: 1
            }}
          >
            <NavigateNextIcon />
          </IconButton>
              </>
            )}
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {currentImageIndex + 1} / {images.length}
            </Typography>
          </Box>
        </Box>
      </Dialog>

      {/* Image Regeneration Modal */}
      <Dialog
        open={regenerateDialogOpen}
        onClose={handleCloseRegenerateDialog}
        maxWidth={aiChatOpen ? false : "lg"}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            border: '2px solid #4A90E2',
            maxWidth: aiChatOpen ? `calc(100% - 64px)` : '1200px',
            width: aiChatOpen ? `calc(1200px + ${DRAWER_WIDTH}px)` : 'auto',
            transition: 'width 0.3s ease, max-width 0.3s ease'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          position: 'relative'
        }}>
          <IconButton
            onClick={handleCloseRegenerateDialog}
            size="large"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#E74C3C',
              '&:hover': {
                backgroundColor: 'rgba(231, 76, 60, 0.1)',
                color: '#C0392B'
              },
              '&:focus': {
                color: '#E74C3C'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 32 }} />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, pr: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '20px' }}>
              Régénérer l'image
            </Typography>
              <Button
                onClick={() => setAiChatOpen(!aiChatOpen)}
                variant={aiChatOpen ? "contained" : "outlined"}
                startIcon={<SmartToyIcon />}
                size="small"
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  ...(aiChatOpen && {
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                    }
                  })
                }}
              >
                Chat IA
              </Button>
              <Button
                onClick={handleValidateImage}
                variant="contained"
                startIcon={<CheckCircle />}
                size="small"
                sx={{
                  background: (() => {
                    const images = block.bloc_data?.images || block.content?.images || [];
                    const currentImage = images[selectedImageIndex];
                    const isValidated = currentImage?.status_seance === 'validated';
                    return isValidated 
                      ? 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)'
                      : 'linear-gradient(135deg, #27AE60 0%, #229954 100%)';
                  })(),
                  borderRadius: '20px',
                  px: 2,
                  py: 0.75,
                  fontWeight: 'bold',
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    background: (() => {
                      const images = block.bloc_data?.images || block.content?.images || [];
                      const currentImage = images[selectedImageIndex];
                      const isValidated = currentImage?.status_seance === 'validated';
                      return isValidated 
                        ? 'linear-gradient(135deg, #C0392B 0%, #A93226 100%)'
                        : 'linear-gradient(135deg, #229954 0%, #1E8449 100%)';
                    })(),
                    transform: 'translateY(-2px)',
                    boxShadow: (() => {
                      const images = block.bloc_data?.images || block.content?.images || [];
                      const currentImage = images[selectedImageIndex];
                      const isValidated = currentImage?.status_seance === 'validated';
                      return isValidated 
                        ? '0 8px 25px rgba(231, 76, 60, 0.4)'
                        : '0 8px 25px rgba(39, 174, 96, 0.4)';
                    })()
                  }
                }}
              >
                {(() => {
                  const images = block.bloc_data?.images || block.content?.images || [];
                  const currentImage = images[selectedImageIndex];
                  const isValidated = currentImage?.status_seance === 'validated';
                  return isValidated ? 'Invalider' : 'Valider';
                })()}
              </Button>
            </Box>
            
            {/* Controls Section */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
              <Button
                onClick={() => setShowAdvancedParams(!showAdvancedParams)}
                variant="outlined"
                size="small"
                startIcon={showAdvancedParams ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                endIcon={<SettingsIcon />}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  borderColor: '#6b7280',
                  color: '#6b7280',
                  '&:hover': {
                    borderColor: '#4b5563',
                    backgroundColor: '#f9fafb'
                  }
                }}
              >
                Paramètres avancés
              </Button>
              
              {showAdvancedParams && (
                <>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Modèle</InputLabel>
                    <Select
                      value={regenerateModel}
                      label="Modèle"
                      onChange={(e) => setRegenerateModel(e.target.value)}
                      sx={{ borderRadius: '8px' }}
                      disabled={modelsLoading}
                    >
                      {imageEditingModels.map((model) => (
                        <MenuItem key={model.id} value={model.id}>
                          {model.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Style</InputLabel>
                    <Select
                      value={regenerateStyle}
                      label="Style"
                      onChange={(e) => setRegenerateStyle(e.target.value)}
                      sx={{ borderRadius: '8px' }}
                    >
                      <MenuItem value="cartoon">Cartoon</MenuItem>
                      <MenuItem value="realistic">Réaliste</MenuItem>
                      <MenuItem value="semi-realistic">Semi-réaliste</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Ratio</InputLabel>
                    <Select
                      value={regenerateAspectRatio}
                      label="Ratio"
                      onChange={(e) => setRegenerateAspectRatio(e.target.value)}
                      sx={{ borderRadius: '8px' }}
                    >
                      <MenuItem value="16:9">16:9</MenuItem>
                      <MenuItem value="1:1">1:1</MenuItem>
                      <MenuItem value="9:16">9:16</MenuItem>
                      <MenuItem value="4:3">4:3</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Nb. Variation</InputLabel>
                    <Select
                      value={regenerateNbVariations}
                      label="Nb. Variation"
                      onChange={(e) => setRegenerateNbVariations(e.target.value)}
                      sx={{ borderRadius: '8px' }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                        <MenuItem key={num} value={num}>{num}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ 
          display: 'flex', 
          gap: 2, 
          p: 3,
          position: 'relative',
          overflow: 'hidden',
          height: 'calc(100vh - 250px)',
          maxHeight: 'calc(100vh - 250px)',
          minHeight: '550px'
        }}>
          <Box sx={{ 
            flex: 1, 
            minWidth: 0,
            transition: 'flex 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            pr: 1,
            height: '100%',
            maxHeight: '100%'
          }}>
          {/* Block Information Section - Scrollable */}
          {(() => {
            const notes = block.bloc_data?.notes || block.content?.notes || block.layers || [];
            if (notes.length === 0) return null;
            
            return (
            <Box sx={{ 
              mb: 2, 
              maxHeight: '150px', 
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 1
            }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2, color: '#34495E' }}>
                Informations du bloc
              </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {notes.map((note, index) => {
                    // Handle both new API structure (bloc_data.notes) and old structure (layers)
                    const noteData = note.title ? note : {
                      id: `note_${index}`,
                      title: note.title || `Note ${index + 1}`,
                      content: { text: note.content || note.text || 'No content generated yet' }
                    };
                    
                    return (
                      <Box
                        key={noteData.id || index}
                sx={{
                          padding: '16px',
                          backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                          border: '1px solid #e1e8ed'
                        }}
                      >
                        {/* Note header */}
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: '#2C3E50',
                            marginBottom: '12px'
                          }}
                        >
                          {noteData.title}
                        </Typography>
                        
                        {/* Note content */}
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: '#2C3E50', 
                            lineHeight: 1.6,
                            marginBottom: '8px'
                          }}
                        >
                          {noteData.content?.text || noteData.content || 'No content generated yet'}
                        </Typography>
                        
                        {/* Character count */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>
                          <span>
                            {(noteData.content?.text || noteData.content || '').length} characters
                          </span>
                          <span style={{
                            color: (noteData.content?.text || noteData.content || '').length > 500 ? '#dc2626' : '#6b7280',
                            fontWeight: (noteData.content?.text || noteData.content || '').length > 500 ? 'bold' : 'normal'
                          }}>
                            Max: 500
                          </span>
            </Box>
                      </Box>
                    );
                  })}
                </Box>
            </Box>
            );
          })()}

          {/* Prompt and Image Section - Fixed height, no scroll */}
          <Box sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden'
          }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ flex: '1 1 40%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  value={regeneratePrompt}
                  onChange={(e) => setRegeneratePrompt(e.target.value)}
                  placeholder="Prompt de generation d'image"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      border: '2px solid #000',
                      height: '100%',
                      '& textarea': {
                        height: '100% !important',
                        overflow: 'auto !important'
                      }
                    }
                  }}
                />
                {/* Régénérer button under prompt */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', flexShrink: 0, pl: 3 }}>
                  <Button
                    onClick={handleRegenerateImage}
                    variant="contained"
                    disabled={!regeneratePrompt.trim() || isRegenerating || !regenerateModel}
                    startIcon={isRegenerating ? <CircularProgress size={20} color="inherit" /> : <ImageIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                      borderRadius: '25px',
                      px: 3,
                      py: 1,
                      fontWeight: 'bold',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #357ABD 0%, #2E6DA4 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(74, 144, 226, 0.4)'
                      },
                      '&:disabled': {
                        background: '#BDBDBD',
                        transform: 'none',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    {isRegenerating ? 'Régénération...' : `Régénérer l'image ${selectedImageIndex + 1}`}
                  </Button>
                </Box>
              </Box>
              <Box sx={{ flex: '1 1 60%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Image Counter and Title on same row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  {/* Image Title - Editable on double-click */}
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: 1, minHeight: '32px', maxWidth: '100%' }}>
                  {editingTitleIndex === selectedImageIndex ? (
                    <TextField
                      autoFocus
                      value={imageTitles.get(selectedImageIndex) || ''}
                      onChange={(e) => {
                        const newTitles = new Map(imageTitles);
                        newTitles.set(selectedImageIndex, e.target.value);
                        setImageTitles(newTitles);
                      }}
                      onBlur={() => {
                        handleSaveImageTitle(selectedImageIndex);
                        setEditingTitleIndex(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveImageTitle(selectedImageIndex);
                          setEditingTitleIndex(null);
                        } else if (e.key === 'Escape') {
                          setEditingTitleIndex(null);
                        }
                      }}
                      placeholder="Titre de l'image"
                      size="small"
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          height: '32px',
                          '& input': {
                            py: 0.5,
                          }
                        }
                      }}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        gap: 0.5, 
                        alignItems: 'center', 
                        flex: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          '& .title-text': {
                            color: '#4A90E2',
                          }
                        }
                      }}
                      onDoubleClick={() => setEditingTitleIndex(selectedImageIndex)}
                    >
                      <Typography 
                        variant="body1" 
                        className="title-text"
                        sx={{ 
                          color: imageTitles.get(selectedImageIndex) ? '#2C3E50' : '#9CA3AF',
                          fontStyle: imageTitles.get(selectedImageIndex) ? 'normal' : 'italic',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          flex: 1,
                          transition: 'color 0.2s'
                        }}
                      >
                        {imageTitles.get(selectedImageIndex) || 'Double-cliquez pour ajouter un titre'}
                      </Typography>
                    </Box>
                  )}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#34495E', whiteSpace: 'nowrap' }}>
                    Image {selectedImageIndex + 1} / {images.length}
                  </Typography>
                </Box>
                <Box
                onClick={() => handleOpenCarousel(selectedImageIndex)}
                  sx={{
                  minWidth: 0,
                  flex: 1,
                  minHeight: 0,
                    border: (() => {
                      const images = block.bloc_data?.images || block.content?.images || [];
                      const currentImage = images[selectedImageIndex];
                      const isValidated = currentImage?.status_seance === 'validated';
                      return isValidated ? '4px solid #27AE60' : '2px solid #000';
                    })(),
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#F0F0F0',
                    position: 'relative',
                  overflow: 'auto',
                    boxShadow: (() => {
                      const images = block.bloc_data?.images || block.content?.images || [];
                      const currentImage = images[selectedImageIndex];
                      const isValidated = currentImage?.status_seance === 'validated';
                      return isValidated ? '0 0 0 3px rgba(39, 174, 96, 0.2)' : 'none';
                    })(),
                  cursor: 'pointer',
                  '&:hover .carousel-nav-button': {
                    opacity: 1
                  },
                  '&:hover': {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: 'rgba(0, 0, 0, 0.1)',
                      transition: 'background-color 0.2s ease',
                      pointerEvents: 'none'
                    }
                  }
                }}
              >
                {images.length > 1 && (
                  <>
                    <IconButton
                      className="carousel-nav-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
                      }}
                      sx={{
                        position: 'absolute',
                        left: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        opacity: 0.3,
                        transition: 'opacity 0.2s ease',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.7)',
                          opacity: 1
                        }
                      }}
                    >
                      <NavigateBeforeIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                    <IconButton
                      className="carousel-nav-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
                      }}
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        opacity: 0.3,
                        transition: 'opacity 0.2s ease',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.7)',
                          opacity: 1
                        }
                      }}
                    >
                      <NavigateNextIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                  </>
                )}
                  {(() => {
                    const selectedImage = images[selectedImageIndex];
                  const imageUrl = selectedImage?.img_url || selectedImage?.original_url || selectedImage?.url;
                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={`Image ${selectedImageIndex + 1}`}
                        style={{
                        maxWidth: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        pointerEvents: 'none',
                        display: 'block'
                        }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
                        IMAGE
                      </Typography>
                    );
                  })()}
                  
                  {/* Favorite button on image in regeneration dialog - top-left corner */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(selectedImageIndex);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 10,
                      bgcolor: 'white',
                      width: '36px',
                      height: '36px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      color: (() => {
                        const images = block.bloc_data?.images || block.content?.images || [];
                        const currentImage = images[selectedImageIndex];
                        return currentImage?.favourite === true ? '#FFD700' : '#BDC3C7';
                      })(),
                      '&:hover': {
                        bgcolor: '#F5F5F5',
                        transform: 'scale(1.1)',
                        color: '#FFD700'
                      }
                    }}
                  >
                    {(() => {
                      const images = block.bloc_data?.images || block.content?.images || [];
                      const currentImage = images[selectedImageIndex];
                      return currentImage?.favourite === true ? (
                        <StarIcon sx={{ fontSize: '28px' }} />
                      ) : (
                        <StarBorderIcon sx={{ fontSize: '28px' }} />
                      );
                    })()}
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>
          </Box>
          
          {/* AI Chat Sidebar */}
          {aiChatOpen && (
            <Box sx={{ 
              width: DRAWER_WIDTH, 
              flexShrink: 0,
              borderLeft: '1px solid #e0e0e0',
              ml: 2,
              height: '100%',
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
                maxHeight: '100%',
                '& > div': {
                  position: 'relative !important',
                  top: '0 !important',
                  height: '100% !important',
                  maxHeight: '100% !important',
                  flex: '1 1 auto !important',
                  overflow: 'hidden !important',
                  // Override AIChat root Box styles
                  '&:first-of-type': {
                    height: '100% !important',
                    maxHeight: '100% !important',
                    display: 'flex !important',
                    flexDirection: 'column !important',
                    boxShadow: 'none !important',
                    borderLeft: 'none !important',
                    position: 'relative !important',
                    alignSelf: 'auto !important'
                  }
                }
              }}>
                <AIChat
                  open={aiChatOpen}
                  onClose={() => setAiChatOpen(false)}
                  seanceId={sessionId}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Images Confirmation Dialog */}
      <Dialog
        open={deleteImagesDialogOpen}
        onClose={handleCloseDeleteImagesDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            '& .MuiDialog-paper': {
              borderRadius: '20px'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0'
        }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <WarningIcon />
            Confirmation de suppression
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2C3E50' }}>
            Êtes-vous sûr de vouloir supprimer {selectedImageIndices.size} image{selectedImageIndices.size > 1 ? 's' : ''} ?
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
            Cette action est irréversible. Les images sélectionnées seront définitivement supprimées.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Button
            onClick={handleCloseDeleteImagesDialog}
            variant="outlined"
            disabled={isDeletingImages}
            sx={{
              borderRadius: '25px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleDeleteSelectedImages}
            variant="contained"
            disabled={isDeletingImages}
            startIcon={isDeletingImages ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
              borderRadius: '25px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #C62828 0%, #B71C1C 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(229, 57, 53, 0.4)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {isDeletingImages ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}